import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Package, Ticket as TicketIcon, TrendingUp, ShoppingCart, MoreVertical, AlertTriangle, ShieldOff, Recycle, Eraser, ShieldCheck, Activity, RefreshCw } from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import StatusPill from "@/components/StatusPill";
import OrderDialog from "@/components/OrderDialog";
import IncidentDialog from "@/components/IncidentDialog";
import RemoteAssistDialog from "@/components/RemoteAssistDialog";
import DeviceHealthDrawer, { DexBadge } from "@/components/DeviceHealthDrawer";
import BuybackDialog from "@/components/BuybackDialog";
import ArchitectureFlow from "@/components/ArchitectureFlow";
import OnboardingCopilot from "@/components/OnboardingCopilot";
import { formatDate, displayReqNumber } from "@/lib/format";
import { useBranding } from "@/lib/branding";

export default function Home() {
  const { persona, userName, userEmail } = usePersona();
  const { brand } = useBranding();
  const [devices, setDevices] = useState([]);
  const [myDevices, setMyDevices] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [refreshRadar, setRefreshRadar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderDevice, setOrderDevice] = useState(null);
  const [assistOpen, setAssistOpen] = useState(false);
  const [incident, setIncident] = useState({ open: false, category: "Incident", deviceRef: null });
  const [healthDrawer, setHealthDrawer] = useState(null);
  const [buyback, setBuyback] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      endpoints.devices({ persona: persona.id }),
      endpoints.dexDevices(persona.id),
      endpoints.tickets(userEmail ? { persona: persona.id, user_email: userEmail } : { persona: persona.id }),
      endpoints.reports(persona.id, userEmail),
      endpoints.refreshRadar(persona.id),
    ]).then(([d, md, t, s, rr]) => {
      setDevices(d); setMyDevices(md); setTickets(t); setSummary(s); setRefreshRadar(rr); setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [persona.id, userEmail]);

  const quickOrderDevice = devices[0];
  const kpis = summary?.kpis;

  const openIncident = (category, deviceRef) => setIncident({ open: true, category, deviceRef });

  return (
    <div className="space-y-8" data-testid="home-page">
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-md border border-slate-200 bg-[#0033A0]">
        <div className="corp-grid absolute inset-0 opacity-20" />
        <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-6 p-8">
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-white/10 backdrop-blur rounded-md text-xs font-semibold">
              <Sparkles size={12} /> NEW FEATURE
            </div>
            <h1 className="mt-3 font-display font-black text-3xl md:text-4xl tracking-tight leading-tight">
              Welcome back, {(userName || "colleague").split(" ")[0]}.<br />
              <span className="text-white/80 font-bold text-2xl md:text-3xl">Remote Assist is now live.</span>
            </h1>
            <p className="mt-3 text-white/70 max-w-lg text-sm">
              Get help from certified {brand.orgName} IT technicians anytime — screen share, chat and diagnostics, all inside Flexspace.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button data-testid="hero-explore-btn" onClick={() => setAssistOpen(true)} className="bg-white text-[#0033A0] hover:bg-white/90 font-semibold">
                Try Remote Assist <ArrowRight size={16} className="ml-2" />
              </Button>
              <Link to="/catalogue">
                <Button data-testid="hero-catalogue-btn" variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white bg-transparent">
                  Browse Catalog
                </Button>
              </Link>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Pending Orders", value: Math.max(1, Number(kpis?.pending_orders ?? 0)), icon: ShoppingCart, testId: "kpi-pending-orders" },
                { label: "Devices in Use", value: kpis?.devices_in_use ?? "—", icon: Package, testId: "kpi-devices-in-use" },
                { label: "Open Tickets", value: kpis?.open_tickets ?? "—", icon: TicketIcon, testId: "kpi-open-tickets" },
                { label: "Awaiting Approval", value: kpis?.pending_approval ?? "—", icon: ShieldCheck, testId: "kpi-pending-approval" },
              ].map((k) => (
                <div key={k.label} className="bg-white/10 backdrop-blur border border-white/20 rounded-md p-4">
                  <div className="flex items-center justify-between text-white/70">
                    <span className="text-[11px] uppercase tracking-widest font-semibold">{k.label}</span>
                    <k.icon size={14} />
                  </div>
                  <div className="text-white font-display font-black text-3xl mt-1" data-testid={k.testId}>{k.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Ecosystem quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ecosystem-tiles">
        {[
          { label: "Device Catalog", desc: "Browse & order", to: "/catalogue", icon: Package, testId: "eco-catalogue" },
          { label: "Report Incident", desc: "Broken device", action: () => openIncident("Incident", null), icon: AlertTriangle, testId: "eco-incident", accent: true },
          { label: "Lost or Stolen", desc: "Report loss", action: () => openIncident("Lost or Stolen", null), icon: ShieldOff, testId: "eco-lost" },
          { label: "Data Wipe / Return", desc: "Certified disposal", action: () => openIncident("Data Wipe", null), icon: Eraser, testId: "eco-wipe" },
        ].map((tile) => {
          const inner = (
            <>
              <div className={`w-10 h-10 rounded-md grid place-items-center ${tile.accent ? "bg-[#DA291C] text-white" : "bg-[#0033A0]/10 text-[#0033A0]"}`}>
                <tile.icon size={20} />
              </div>
              <div className="mt-3 font-semibold text-slate-900 text-sm">{tile.label}</div>
              <div className="text-xs text-slate-500">{tile.desc}</div>
            </>
          );
          return tile.to ? (
            <Link key={tile.label} to={tile.to} data-testid={tile.testId} className="device-card p-4 border border-slate-200 rounded-md bg-white text-left block">
              {inner}
            </Link>
          ) : (
            <button key={tile.label} data-testid={tile.testId} onClick={tile.action} className="device-card p-4 border border-slate-200 rounded-md bg-white text-left">
              {inner}
            </button>
          );
        })}
      </div>

      {/* Onboarding Copilot — personal Day-1 assistant */}
      <OnboardingCopilot />

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-xl text-slate-900">My Devices</h2>
              <p className="text-xs text-slate-500 mt-0.5">Assets currently assigned to you</p>
            </div>
            <Link to="/requests?view=orders" className="text-xs font-semibold text-[#0033A0] hover:underline" data-testid="view-all-devices">View Orders</Link>
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="my-devices-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Device</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Asset Tag</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Device UX</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Location</th>
                  <th className="text-right px-4 py-2.5 font-semibold w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading && Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} className="border-t border-slate-200"><td colSpan={6} className="p-3"><Skeleton className="h-6 w-full" /></td></tr>
                ))}
                {!loading && myDevices.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-500 text-sm">No devices assigned yet.</td></tr>
                )}
                {myDevices.map((d) => (
                  <tr key={d.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <button onClick={() => setHealthDrawer(d.asset_tag)} data-testid={`device-name-${d.asset_tag}`} className="text-left hover:text-[#0033A0]">
                        {d.device_name}
                      </button>
                      {d.relevance && (
                        <div className="mt-0.5 flex items-center gap-1.5 text-[10px]" data-testid={`device-relevance-${d.asset_tag}`}>
                          <span
                            className="font-mono font-bold text-white px-1.5 py-0.5 rounded text-[9px]"
                            style={{ backgroundColor: d.relevance.score >= 75 ? "#0033A0" : d.relevance.score >= 60 ? "#F59E0B" : "#94a3b8" }}
                          >
                            AI {d.relevance.score}
                          </span>
                          <span className="text-slate-500">{d.relevance.label}</span>
                          {d.relevance.copilot_plus_ready && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#0033A0]/10 text-[#0033A0] font-semibold">Copilot+</span>
                          )}
                          <span className="text-slate-400">· {d.relevance.ready_apps} AI apps ready</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{d.asset_tag}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setHealthDrawer(d.asset_tag)} data-testid={`dex-${d.asset_tag}`}>
                        <DexBadge score={d.dex_score} trend={d.dex_trend} />
                      </button>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={d.status} /></td>
                    <td className="px-4 py-3 text-slate-600">{d.location}</td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`device-actions-${d.asset_tag}`} className="h-7 w-7"><MoreVertical size={14} /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setHealthDrawer(d.asset_tag)} data-testid={`act-health-${d.asset_tag}`}>
                            <Activity size={14} className="mr-2 text-[#0033A0]" /> View Health &amp; Self-Heal
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openIncident("Incident", `${d.device_name} · ${d.asset_tag}`)} data-testid={`act-incident-${d.asset_tag}`}>
                            <AlertTriangle size={14} className="mr-2 text-[#DA291C]" /> Report Incident
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openIncident("Lost or Stolen", `${d.device_name} · ${d.asset_tag}`)} data-testid={`act-lost-${d.asset_tag}`}>
                            <ShieldOff size={14} className="mr-2" /> Report Lost / Stolen
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openIncident("Data Wipe", `${d.device_name} · ${d.asset_tag}`)} data-testid={`act-wipe-${d.asset_tag}`}>
                            <Eraser size={14} className="mr-2" /> Request Data-Wipe
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setBuyback(d)} data-testid={`act-buyback-${d.asset_tag}`}>
                            <Recycle size={14} className="mr-2 text-green-600" /> Return / Buyback Quote
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-xl text-slate-900">Quick Order</h2>
              <p className="text-xs text-slate-500 mt-0.5">Recommended for {persona.name}</p>
            </div>
            <Link to="/catalogue" className="text-xs font-semibold text-[#0033A0] hover:underline" data-testid="view-all-catalogue">View All</Link>
          </div>
          {loading || !quickOrderDevice ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="border border-slate-200 rounded-md p-4 flex-1 flex flex-col">
              <div className="aspect-[16/9] rounded-md overflow-hidden bg-slate-100">
                <img src={quickOrderDevice.image_url} alt={quickOrderDevice.name} className="w-full h-full object-cover" />
              </div>
              <div className="mt-3">
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{quickOrderDevice.brand}</div>
                <div className="font-semibold text-slate-900">{quickOrderDevice.name}</div>
                <div className="text-xs text-slate-500 mt-1">{quickOrderDevice.specs.slice(0, 2).join(" · ")}</div>
                <div className="text-xs text-slate-500 mt-1">Est. ${quickOrderDevice.price.toLocaleString()} {quickOrderDevice.price > 1000 && <span className="text-[#DA291C] font-semibold">· needs approval</span>}</div>
              </div>
              <Button
                data-testid="quick-order-btn"
                onClick={() => setOrderDevice(quickOrderDevice)}
                className="mt-4 bg-[#0033A0] hover:bg-[#002573] text-white w-full"
              >
                Customize &amp; Order
              </Button>
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6 border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display font-bold text-xl text-slate-900">Open Tickets</h2>
            <p className="text-xs text-slate-500 mt-0.5">Active ServiceNow requests</p>
          </div>
          <Link to="/requests?view=all" className="text-xs font-semibold text-[#0033A0] hover:underline" data-testid="view-all-tickets">View All</Link>
        </div>
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="tickets-preview-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">REQ #</th>
                <th className="text-left px-4 py-2.5 font-semibold">Subject</th>
                <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                <th className="text-left px-4 py-2.5 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 2 }).map((_, i) => (
                <tr key={i} className="border-t border-slate-200"><td colSpan={4} className="p-3"><Skeleton className="h-6 w-full" /></td></tr>
              ))}
              {!loading && tickets.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-slate-500 text-sm">No open tickets. Place an order to create one.</td></tr>
              )}
              {tickets.slice(0, 5).map((t) => {
                const displayNum = displayReqNumber(t);
                return (
                <tr key={t.id} className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => (window.location.href = `/tickets/${t.id}`)}>
                  <td className="px-4 py-3">
                    <Link to={`/tickets/${t.id}`} className="text-[#0033A0] font-mono text-xs font-semibold hover:underline" data-testid={`ticket-link-${displayNum}`}>
                      {displayNum}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-900">{t.subject}</td>
                  <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(t.created_at)}</td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-6 border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display font-bold text-xl text-slate-900 flex items-center gap-2">
              <RefreshCw size={18} className="text-[#0033A0]" /> Refresh Radar
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Devices approaching or past the 3-year refresh cycle</p>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : refreshRadar.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6" data-testid="refresh-empty">
            No devices need refreshing. All assets are within the 3-year cycle.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3" data-testid="refresh-radar-list">
            {refreshRadar.map((d) => (
              <div key={d.asset_tag} className="border border-slate-200 rounded-md p-3 flex items-center gap-3">
                {d.suggested_replacement && (
                  <div className="w-14 h-14 rounded-md overflow-hidden bg-slate-100 shrink-0">
                    <img src={d.suggested_replacement.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono text-slate-500">{d.asset_tag}</div>
                  <div className="font-semibold text-slate-900 text-sm truncate">{d.device_name}</div>
                  <div className={`text-[11px] font-semibold ${d.refresh_status === "Overdue" ? "text-[#DA291C]" : "text-amber-600"}`}>
                    {d.refresh_status} · {d.age_months}mo old
                  </div>
                  {d.suggested_replacement && (
                    <div className="text-[11px] text-slate-500 truncate">Suggested: {d.suggested_replacement.name}</div>
                  )}
                </div>
                {d.suggested_replacement && (
                  <Button
                    size="sm"
                    data-testid={`refresh-order-${d.asset_tag}`}
                    onClick={() => setOrderDevice(d.suggested_replacement)}
                    className="bg-[#0033A0] hover:bg-[#002573] text-white shrink-0"
                  >
                    Order
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <ArchitectureFlow />

      <OrderDialog device={orderDevice} onClose={() => setOrderDevice(null)} onSuccess={() => { setOrderDevice(null); load(); }} />
      <RemoteAssistDialog open={assistOpen} onOpenChange={setAssistOpen} />
      <IncidentDialog open={incident.open} defaultCategory={incident.category} deviceRef={incident.deviceRef}
        onClose={() => setIncident({ open: false, category: "Incident", deviceRef: null })}
        onSuccess={() => { setIncident({ open: false, category: "Incident", deviceRef: null }); load(); }} />
      <DeviceHealthDrawer
        assetTag={healthDrawer}
        open={!!healthDrawer}
        onOpenChange={(o) => { if (!o) setHealthDrawer(null); }}
        onChanged={load}
      />
      <BuybackDialog open={!!buyback} onOpenChange={(o) => { if (!o) setBuyback(null); }} device={buyback} />
    </div>
  );
}
